import { useCallback, useEffect, useState } from "react";
import {
  ManageUsersActionType,
  useManageUsersContext,
} from "../../../../context/ManageUsersContext";
import { Gender } from "../../../../models/base-user.interface";
import { IUser, UserStatus } from "../../../../models/users.interface";
import { getFullNameWithTitle } from "../../../../utils/userUtil";
import Table, { TableRef } from "../../../Shared/Table/Table";
import managePatientsTableColumns from "./managePatientsTableColumns";

export type PatientsTableItem = {
  id: string;
  entity?: string | null;
  prescribingPhysician: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender?: Gender;
  phone: string;
  lastEcgDate: string;
  deviceSN: string;
  creationDate: string;
  status: UserStatus;
  hasPacemaker?: boolean;
};

const mapUsersToTableUsers = (users: IUser[]): PatientsTableItem[] => {
  return users.map((user) => {
    const tableRow: PatientsTableItem = {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone,
      entity: user.organization?.name,
      creationDate: user.createdAt,
      status: user.status,
      dateOfBirth: user.dateOfBirth ?? "",
      deviceSN: user.deviceSn ?? "",
      gender: user.gender,
      lastEcgDate: user.lastEcgDate ?? "",
      prescribingPhysician: getFullNameWithTitle(
        user.prescribingPhysician?.title,
        user.prescribingPhysician?.firstName,
        user.prescribingPhysician?.lastName
      ),
      hasPacemaker: user.hasPacemaker,
    };
    return tableRow;
  });
};

type ManagePatientsTableProps = {
  itemsPerPage?: number;
};

const ManagePatientsTable = ({ itemsPerPage }: ManagePatientsTableProps) => {
  const {
    dispatch,
    state: { users, selectedUser, usersTableRef },
  } = useManageUsersContext();

  const [data, setData] = useState<PatientsTableItem[]>([]);

  useEffect(() => {
    const tableData = mapUsersToTableUsers(users);
    setData(tableData);
  }, [users]); // eslint-disable-line

  const refChangeHandler = useCallback(
    (ref: TableRef) => {
      if (ref !== null) {
        dispatch({
          type: ManageUsersActionType.SET_TABLE_REF,
          payload: ref,
        });
      }
    },
    [dispatch]
  );

  const userSelectedHandler = (user: PatientsTableItem) => {
    const curUser = users.find((u) => u.id === user.id) ?? null;
    dispatch({
      type: ManageUsersActionType.SET_SELECTED_USER,
      payload: curUser,
    });
  };

  const tableCols = managePatientsTableColumns(usersTableRef?.globalFilter);

  return (
    <Table<PatientsTableItem>
      tableData={data}
      tableColumns={tableCols}
      ref={refChangeHandler}
      itemsPerPage={itemsPerPage}
      onRowSelect={userSelectedHandler}
      selectedRowId={selectedUser?.id}
      sortable
    />
  );
};

export default ManagePatientsTable;
